"use client";

import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth } from "./Auth";
import ModernNavigation from "./ModernNavigation";
import {
  Palette,
  FileText,
  Video,
  Brain,
  Code,
  Languages,
  TrendingUp,
  Target,
  Mail,
  Globe,
  Zap,
  BarChart3,
  Star,
  Clock,
  Award,
  Activity,
  Settings,
  LogOut,
  User,
  Sparkles,
  Rocket,
  CheckCircle,
  Search
} from "lucide-react";

interface Tool {
  id: string;
  name: string;
  icon: any;
  description: string;
  color: string;
  category: string;
  usage?: number;
  lastUsed?: string;
}

export default function UltraModernUserPage() {
  const { user, logout } = useAuth();
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [viewMode, setViewMode] = useState<"grid" | "list">("grid");
  const [recentTools, setRecentTools] = useState<string[]>([]);

  useEffect(() => {
    const saved = localStorage.getItem('recentTools');
    if (saved) {
      setRecentTools(JSON.parse(saved));
    }
  }, []);

  const tools: Tool[] = [
    {
      id: "image",
      name: "Geração de Imagens",
      icon: Palette,
      description: "Crie imagens impressionantes com IA",
      color: "from-blue-500 to-cyan-500",
      category: "criacao",
      usage: 45,
      lastUsed: "2 horas atrás"
    },
    {
      id: "pitch",
      name: "Gerador de Pitch",
      icon: FileText,
      description: "Pitches de vendas persuasivos",
      color: "from-green-500 to-emerald-500",
      category: "criacao",
      usage: 32,
      lastUsed: "1 dia atrás"
    },
    {
      id: "video",
      name: "Script de Vídeo",
      icon: Video,
      description: "Scripts profissionais para vídeos",
      color: "from-purple-500 to-pink-500",
      category: "criacao",
      usage: 28,
      lastUsed: "3 dias atrás"
    },
    {
      id: "viral",
      name: "Conteúdo Viral",
      icon: TrendingUp,
      description: "Crie conteúdo com potencial viral",
      color: "from-orange-500 to-red-500",
      category: "marketing",
      usage: 15,
      lastUsed: "1 semana atrás"
    },
    {
      id: "black-friday",
      name: "Black Friday",
      icon: Target,
      description: "Ofertas para Black Friday",
      color: "from-red-600 to-orange-600",
      category: "marketing",
      usage: 8,
      lastUsed: "2 semanas atrás"
    },
    {
      id: "email",
      name: "Email Sequence",
      icon: Mail,
      description: "Sequências de email automatizadas",
      color: "from-blue-600 to-indigo-600",
      category: "marketing",
      usage: 22,
      lastUsed: "5 dias atrás"
    },
    {
      id: "funnel",
      name: "Funil de Vendas",
      icon: BarChart3,
      description: "Otimize seu processo de vendas",
      color: "from-green-600 to-teal-600",
      category: "marketing",
      usage: 18,
      lastUsed: "4 dias atrás"
    },
    {
      id: "landing",
      name: "Landing Page",
      icon: Globe,
      description: "Páginas de alta conversão",
      color: "from-purple-600 to-pink-600",
      category: "marketing",
      usage: 12,
      lastUsed: "6 dias atrás"
    },
    {
      id: "sentiment",
      name: "Análise de Sentimento",
      icon: Brain,
      description: "Analise emoções em textos",
      color: "from-red-500 to-orange-500",
      category: "analise",
      usage: 35,
      lastUsed: "1 dia atrás"
    },
    {
      id: "code",
      name: "Gerador de Código",
      icon: Code,
      description: "Gere código em múltiplas linguagens",
      color: "from-yellow-500 to-amber-500",
      category: "tecnico",
      usage: 25,
      lastUsed: "3 horas atrás"
    },
    {
      id: "translate",
      name: "Tradutor",
      icon: Languages,
      description: "Traduza com contexto cultural",
      color: "from-indigo-500 to-blue-500",
      category: "tecnico",
      usage: 19,
      lastUsed: "2 dias atrás"
    }
  ];

  const categories = [
    { id: "all", name: "Todas", icon: Sparkles },
    { id: "criacao", name: "Criação", icon: Palette },
    { id: "marketing", name: "Marketing", icon: Target },
    { id: "analise", name: "Análise", icon: Brain },
    { id: "tecnico", name: "Técnico", icon: Code }
  ];

  const filteredTools = tools.filter(tool => {
    const matchesCategory = selectedCategory === "all" || tool.category === selectedCategory;
    const matchesSearch = tool.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         tool.description.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const handleToolClick = (toolId: string) => {
    const newRecent = [toolId, ...recentTools.filter(id => id !== toolId)].slice(0, 5);
    setRecentTools(newRecent);
    localStorage.setItem('recentTools', JSON.stringify(newRecent));
  };

  const userStats = {
    totalUsage: tools.reduce((sum, tool) => sum + (tool.usage || 0), 0),
    favoriteCategory: "criacao",
    streak: 7,
    achievements: 12
  };

  if (!user) {
    return (
      <motion.div 
        className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
      >
        <div className="text-center">
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
            className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-6"
          >
            <User className="w-8 h-8 text-purple-600" />
          </motion.div>
          <motion.h1 
            className="text-3xl font-bold text-gray-900 mb-3"
            whileHover={{ scale: 1.05 }}
          >
            Acesso Restrito
          </motion.h1>
          <motion.p 
            className="text-gray-600 text-lg mb-6"
            whileHover={{ scale: 1.02 }}
          >
            Faça login para acessar seu painel
          </motion.p>
          <motion.a
            href="/login"
            className="inline-flex items-center px-8 py-4 bg-gradient-to-r from-purple-600 to-pink-600 text-white font-bold rounded-xl hover:from-purple-700 hover:to-pink-700 transition-all duration-300 shadow-lg hover:shadow-xl"
            whileHover={{ scale: 1.05, y: -2 }}
            whileTap={{ scale: 0.95 }}
          >
            <Rocket className="w-5 h-5 mr-2" />
            Fazer Login
          </motion.a>
        </div>
      </motion.div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      <ModernNavigation />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Enhanced User Header */}
        <motion.div 
          className="mb-8 sm:mb-12"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <div className="bg-white rounded-3xl shadow-2xl p-8 border border-gray-100">
            <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-6">
              <div className="flex items-center gap-6">
                <motion.div
                  animate={{ rotate: [0, 5, -5, 0] }}
                  transition={{ duration: 4, repeat: Infinity }}
                  className="w-20 h-20 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full flex items-center justify-center shadow-xl"
                >
                  <User className="w-10 h-10 text-white" />
                </motion.div>
                <div>
                  <motion.h1 
                    className="text-3xl font-bold text-gray-900 mb-2"
                    whileHover={{ scale: 1.02 }}
                  >
                    Bem-vindo, {user.name}! 👋
                  </motion.h1>
                  <div className="flex items-center gap-4 text-gray-600">
                    <motion.span 
                      className="flex items-center gap-1"
                      whileHover={{ scale: 1.05 }}
                    >
                      <Star className="w-4 h-4 text-yellow-500" />
                      Nível Avançado
                    </motion.span>
                    <motion.span 
                      className="flex items-center gap-1"
                      whileHover={{ scale: 1.05 }}
                    >
                      <Activity className="w-4 h-4 text-green-500" />
                      {userStats.streak} dias seguidos
                    </motion.span>
                  </div>
                </div>
              </div>
              
              <motion.button
                onClick={logout}
                className="flex items-center gap-2 px-6 py-3 bg-red-50 text-red-600 rounded-xl hover:bg-red-100 transition-all duration-200 font-medium"
                whileHover={{ scale: 1.05, x: 5 }}
                whileTap={{ scale: 0.95 }}
              >
                <LogOut className="w-5 h-5" />
                Sair
              </motion.button>
            </div>
          </div>
        </motion.div>

        {/* User Stats Cards */}
        <motion.div 
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.5 }}
        >
          <motion.div
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ delay: 0.3, duration: 0.5 }}
            whileHover={{ scale: 1.05, y: -5 }}
            className="bg-white rounded-2xl p-6 shadow-xl border border-gray-100"
          >
            <div className="flex items-center justify-between mb-4">
              <motion.div
                animate={{ rotate: [0, 360] }}
                transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
              >
                <Activity className="w-8 h-8 text-purple-600" />
              </motion.div>
              <motion.span 
                className="text-2xl font-bold text-gray-900"
                whileHover={{ scale: 1.2 }}
              >
                {userStats.totalUsage}
              </motion.span>
            </div>
            <p className="text-gray-600 font-medium">Usos Totais</p>
          </motion.div>

          <motion.div
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ delay: 0.4, duration: 0.5 }}
            whileHover={{ scale: 1.05, y: -5 }}
            className="bg-white rounded-2xl p-6 shadow-xl border border-gray-100"
          >
            <div className="flex items-center justify-between mb-4">
              <motion.div
                animate={{ rotate: [0, 360] }}
                transition={{ duration: 25, repeat: Infinity, ease: "linear" }}
              >
                <Target className="w-8 h-8 text-green-600" />
              </motion.div>
              <motion.span 
                className="text-2xl font-bold text-gray-900"
                whileHover={{ scale: 1.2 }}
              >
                {userStats.favoriteCategory}
              </motion.span>
            </div>
            <p className="text-gray-600 font-medium">Categoria Favorita</p>
          </motion.div>

          <motion.div
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ delay: 0.5, duration: 0.5 }}
            whileHover={{ scale: 1.05, y: -5 }}
            className="bg-white rounded-2xl p-6 shadow-xl border border-gray-100"
          >
            <div className="flex items-center justify-between mb-4">
              <motion.div
                animate={{ rotate: [0, 360] }}
                transition={{ duration: 30, repeat: Infinity, ease: "linear" }}
              >
                <Clock className="w-8 h-8 text-orange-600" />
              </motion.div>
              <motion.span 
                className="text-2xl font-bold text-gray-900"
                whileHover={{ scale: 1.2 }}
              >
                {userStats.streak}
              </motion.span>
            </div>
            <p className="text-gray-600 font-medium">Dias Seguidos</p>
          </motion.div>

          <motion.div
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ delay: 0.6, duration: 0.5 }}
            whileHover={{ scale: 1.05, y: -5 }}
            className="bg-white rounded-2xl p-6 shadow-xl border border-gray-100"
          >
            <div className="flex items-center justify-between mb-4">
              <motion.div
                animate={{ rotate: [0, 360] }}
                transition={{ duration: 15, repeat: Infinity, ease: "linear" }}
              >
                <Award className="w-8 h-8 text-yellow-600" />
              </motion.div>
              <motion.span 
                className="text-2xl font-bold text-gray-900"
                whileHover={{ scale: 1.2 }}
              >
                {userStats.achievements}
              </motion.span>
            </div>
            <p className="text-gray-600 font-medium">Conquistas</p>
          </motion.div>
        </motion.div>

        {/* Recent Tools */}
        {recentTools.length > 0 && (
          <motion.div 
            className="mb-8"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.4, duration: 0.5 }}
          >
            <div className="bg-white rounded-2xl shadow-xl p-6 border border-gray-100">
              <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                <Clock className="w-5 h-5 text-purple-600" />
                Usados Recentemente
              </h3>
              <div className="flex gap-3 flex-wrap">
                {recentTools.map((toolId, index) => {
                  const tool = tools.find(t => t.id === toolId);
                  if (!tool) return null;
                  return (
                    <motion.div
                      key={toolId}
                      initial={{ scale: 0, opacity: 0 }}
                      animate={{ scale: 1, opacity: 1 }}
                      transition={{ delay: 0.5 + index * 0.1, duration: 0.3 }}
                      whileHover={{ scale: 1.1, y: -3 }}
                      className={`bg-gradient-to-r ${tool.color} text-white px-4 py-2 rounded-xl font-medium text-sm shadow-lg`}
                    >
                      {tool.name}
                    </motion.div>
                  );
                })}
              </div>
            </div>
          </motion.div>
        )}

        {/* Tools Section */}
        <motion.div 
          className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6, duration: 0.5 }}
        >
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-8 gap-4">
            <motion.h2 
              className="text-2xl font-bold text-gray-900"
              whileHover={{ scale: 1.02 }}
            >
              🚀 Suas Ferramentas IA
            </motion.h2>
            <div className="flex items-center gap-3">
              <motion.button
                onClick={() => setViewMode("grid")}
                className={`p-3 rounded-xl transition-all duration-200 ${
                  viewMode === "grid" 
                    ? "bg-purple-100 text-purple-600 shadow-lg" 
                    : "text-gray-500 hover:bg-gray-100"
                }`}
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.95 }}
              >
                <div className="grid grid-cols-2 gap-0.5 w-4 h-4">
                  <div className="bg-current rounded-sm"></div>
                  <div className="bg-current rounded-sm"></div>
                  <div className="bg-current rounded-sm"></div>
                  <div className="bg-current rounded-sm"></div>
                </div>
              </motion.button>
              <motion.button
                onClick={() => setViewMode("list")}
                className={`p-3 rounded-xl transition-all duration-200 ${
                  viewMode === "list" 
                    ? "bg-purple-100 text-purple-600 shadow-lg" 
                    : "text-gray-500 hover:bg-gray-100"
                }`}
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.95 }}
              >
                <div className="space-y-1 w-4 h-4">
                  <div className="bg-current h-0.5 rounded"></div>
                  <div className="bg-current h-0.5 rounded"></div>
                  <div className="bg-current h-0.5 rounded"></div>
                </div>
              </motion.button>
            </div>
          </div>

          {/* Search and Filter */}
          <div className="flex flex-col sm:flex-row gap-4 mb-8">
            <motion.div 
              className="flex-1 relative"
              whileHover={{ scale: 1.02 }}
            >
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="🔍 Buscar ferramentas..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-12 pr-4 py-4 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent text-lg"
              />
            </motion.div>
            <motion.select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="px-6 py-4 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent text-lg font-medium"
              whileHover={{ scale: 1.02 }}
            >
              {categories.map(cat => (
                <option key={cat.id} value={cat.id}>{cat.name}</option>
              ))}
            </motion.select>
          </div>

          {/* Tools Display */}
          <div className={viewMode === "grid" ? "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" : "space-y-4"}>
            <AnimatePresence>
              {filteredTools.map((tool, index) => (
                <motion.div
                  key={tool.id}
                  initial={{ scale: 0, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  exit={{ scale: 0, opacity: 0 }}
                  transition={{ delay: index * 0.05, duration: 0.3 }}
                  onClick={() => handleToolClick(tool.id)}
                  className={`${
                    viewMode === "grid" 
                      ? "bg-white border-2 border-gray-200 rounded-2xl p-6 hover:shadow-2xl hover:-translate-y-2 transition-all duration-300 cursor-pointer"
                      : "bg-white border-2 border-gray-200 rounded-2xl p-6 hover:shadow-xl transition-all duration-300 cursor-pointer"
                  }`}
                  whileHover={{ 
                    scale: 1.03, 
                    y: -5,
                    borderColor: "#8b5cf6"
                  }}
                  whileTap={{ scale: 0.98 }}
                >
                  {viewMode === "grid" ? (
                    <>
                      <motion.div 
                        className={`bg-gradient-to-r ${tool.color} w-14 h-14 rounded-2xl flex items-center justify-center mb-4 shadow-xl`}
                        whileHover={{ rotate: 360, scale: 1.1 }}
                        transition={{ duration: 0.5 }}
                      >
                        <tool.icon className="w-7 h-7 text-white" />
                      </motion.div>
                      <h4 className="font-bold text-lg text-gray-900 mb-2">{tool.name}</h4>
                      <p className="text-gray-600 text-sm mb-3">{tool.description}</p>
                      <div className="flex items-center justify-between text-xs text-gray-500">
                        <span>{tool.usage} usos</span>
                        <span>{tool.lastUsed}</span>
                      </div>
                    </>
                  ) : (
                    <div className="flex items-center">
                      <motion.div 
                        className={`bg-gradient-to-r ${tool.color} w-12 h-12 rounded-xl flex items-center justify-center mr-4`}
                        whileHover={{ rotate: 360, scale: 1.1 }}
                        transition={{ duration: 0.5 }}
                      >
                        <tool.icon className="w-6 h-6 text-white" />
                      </motion.div>
                      <div className="flex-1">
                        <h4 className="font-bold text-lg text-gray-900">{tool.name}</h4>
                        <p className="text-gray-600 text-sm">{tool.description}</p>
                        <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                          <span>{tool.usage} usos</span>
                          <span>{tool.lastUsed}</span>
                        </div>
                      </div>
                      <motion.div
                        animate={{ x: [0, 5, 0] }}
                        transition={{ duration: 1, repeat: Infinity }}
                      >
                        <CheckCircle className="w-5 h-5 text-green-500" />
                      </motion.div>
                    </div>
                  )}
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
